import React, { useState } from 'react';
import { useUser } from '../context/UserContext';
import { useTonAddress } from '@tonconnect/ui-react';
import '../styles/UserDashboard.css';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '@/config/firebase';
import { onAuthStateChanged } from 'firebase/auth';

const createVirtualWalletCallable = httpsCallable(functions, 'createVirtualWallet');
const getVirtualWalletBalanceCallable = httpsCallable(functions, 'getVirtualWalletBalance');
const withdrawTonCallable = httpsCallable(functions, 'withdrawTon');
const getOtherGameTokensCallable = httpsCallable(functions, 'getOtherGameTokens');
const convertShitcoinFromOtherGameCallable = httpsCallable(functions, 'convertShitcoinFromOtherGame');

interface UserDashboardProps {
    onClose: () => void;
}

export const UserDashboard: React.FC<UserDashboardProps> = ({ onClose }) => {
    const { user } = useUser();
    const tonAddress = useTonAddress();
    const [creatingWallet, setCreatingWallet] = useState(false);
    const [tonBalance, setTonBalance] = useState<number | null>(null);
    const [loadingBalance, setLoadingBalance] = useState(false);
    const [withdrawAddress, setWithdrawAddress] = useState("");
    const [withdrawAmount, setWithdrawAmount] = useState("");
    const [withdrawing, setWithdrawing] = useState(false);

    // Cross-Game State
    const [otherBalances, setOtherBalances] = useState<any>(null);
    const [loadingOther, setLoadingOther] = useState(false);
    const [convertAmount, setConvertAmount] = useState("");
    const [converting, setConverting] = useState(false);

    React.useEffect(() => {
        if (user?.walletId) {
            // Wait for Auth to be ready before fetching balance
            // This prevents 401 errors if localStorage hydrates user before Firebase Auth SDK initializes
            const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
                if (firebaseUser) {
                    fetchBalance();
                    fetchOtherBalances();
                }
            });
            return () => unsubscribe();
        }
    }, [user?.walletId, user?.telegramId]);

    const fetchBalance = async () => {
        setLoadingBalance(true);
        try {
            const res = await getVirtualWalletBalanceCallable();
            setTonBalance((res.data as any).balance);
        } catch (e) {
            console.error("Failed to fetch balance:", e);
        } finally {
            setLoadingBalance(false);
        }
    };

    const fetchOtherBalances = async () => {
        if (!user?.telegramId) return;
        setLoadingOther(true);
        try {
            const res = await getOtherGameTokensCallable();
            if ((res.data as any).success) {
                setOtherBalances((res.data as any).balances);
            }
        } catch (e) {
            console.error("Failed to fetch other game balances:", e);
        } finally {
            setLoadingOther(false);
        }
    };

    if (!user) return null;

    const truncateAddress = (address: string) => {
        if (!address) return "";
        return `${address.slice(0, 6)}...${address.slice(-4)}`;
    };

    const copyAddress = async (address: string) => {
        if (address) {
            try {
                await navigator.clipboard.writeText(address);
                alert("Address copied!");
            } catch (err) {
                console.error('Failed to copy text: ', err);
            }
        }
    };

    const createWallet = async () => {
        setCreatingWallet(true);
        try {
            const result = await createVirtualWalletCallable();
            const data = result.data as any;
            if (data.success) {
                alert("Wallet created! " + data.walletId);
            }
        } catch (e: any) {
            console.error("Failed to create wallet:", e);
            alert("Error creating wallet: " + e.message);
        } finally {
            setCreatingWallet(false);
        }
    };

    const handleWithdraw = async () => {
        if (!withdrawAddress || !withdrawAmount) return;
        setWithdrawing(true);
        try {
            const result = await withdrawTonCallable({
                toAddress: withdrawAddress,
                amount: parseFloat(withdrawAmount)
            });
            const data = result.data as any;
            if (data.success) {
                alert("Withdrawal successful! " + data.message);
                setWithdrawAddress("");
                setWithdrawAmount("");
                fetchBalance();
            }
        } catch (e: any) {
            console.error("Withdrawal failed:", e);
            alert("Withdrawal failed: " + (e.message || "Unknown error"));
        } finally {
            setWithdrawing(false);
        }
    };
    const handleConvert = async () => {
        if (!convertAmount || parseFloat(convertAmount) <= 0) return;
        setConverting(true);
        try {
            const result = await convertShitcoinFromOtherGameCallable({
                amount: parseFloat(convertAmount)
            });
            const data = result.data as any;
            if (data.success) {
                alert(`Successfully converted! Gained ${data.buinkGained} Buink.`);
                setConvertAmount("");
                fetchOtherBalances(); // Refresh shitcoin balance
                // Balance update here happens via context update usually, 
                // but since it's a Cloud Function that updates Firestore, 
                // if we have a listener on UserDoc it should update.
            }
        } catch (e: any) {
            console.error("Conversion failed:", e);
            alert("Conversion failed: " + (e.message || "Unknown error"));
        } finally {
            setConverting(false);
        }
    };

    return (
        <div className="dashboard-overlay">
            <div className="dashboard-panel glass-panel">
                <button className="close-btn" onClick={onClose} aria-label="Close">×</button>
                <div className="dashboard-header">
                    <h2>Your Dashboard</h2>
                </div>

                <div className="dashboard-content">
                    {/* User Profile Section */}
                    <div className="dashboard-section profile-section">
                        <div className="user-identity">
                            <div className="avatar-placeholder">{user.username.charAt(0).toUpperCase()}</div>
                            <div className="identity-details">
                                <h3>{user.username}</h3>
                                <div className="connected-status">
                                    {tonAddress ? (
                                        <span className="status-badge connected">Wallet Connected</span>
                                    ) : (
                                        <span className="status-badge disconnected">Wallet Not Connected</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="virtual-wallet-card">
                            <p className="section-label">Virtual Wallet Address</p>
                            {user.virtualWalletId ? (
                                <div className="wallet-copy-row">
                                    <code className="wallet-address-display" onClick={() => copyAddress(user.virtualWalletId as string)} title={user.virtualWalletId as string}>
                                        {truncateAddress(user.virtualWalletId as string)}
                                    </code>
                                    <button onClick={() => copyAddress(user.virtualWalletId as string)} className="icon-btn copy-btn" title="Copy Address">
                                        📋
                                    </button>
                                </div>
                            ) : (
                                <button onClick={createWallet} disabled={creatingWallet} className="action-btn create-wallet-btn">
                                    {creatingWallet ? "Creating..." : "Create Virtual Wallet"}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Balances Grid */}
                    <div className="dashboard-section balances-section">
                        <h3>Assets</h3>
                        <div className="balances-grid">
                            <div className="balance-card">
                                <span className="balance-icon">💎</span>
                                <div className="balance-info">
                                    <span className="balance-value">{user.points || 0}</span>
                                    <span className="balance-label">Points</span>
                                </div>
                            </div>
                            <div className="balance-card">
                                <span className="balance-icon">🍌</span>
                                <div className="balance-info">
                                    <span className="balance-value">{user.bananium || 0}</span>
                                    <span className="balance-label">Bananium</span>
                                </div>
                            </div>
                            <div className="balance-card">
                                <span className="balance-icon">🪙</span>
                                <div className="balance-info">
                                    <span className="balance-value">{user.token404 || 0}</span>
                                    <span className="balance-label">Buink</span>
                                </div>
                            </div>
                            <div className="balance-card highlight">
                                <span className="balance-icon">💎</span>
                                <div className="balance-info">
                                    <span className="balance-value">{loadingBalance ? "..." : (tonBalance !== null ? Number(tonBalance || 0).toFixed(4) : "0.00")}</span>
                                    <span className="balance-label">TON (Virtual)</span>
                                </div>
                            </div>
                            <div className="balance-card">
                                <span className="balance-icon">💩</span>
                                <div className="balance-info">
                                    <span className="balance-value">{loadingOther ? "..." : (otherBalances?.shitcoin || 0)}</span>
                                    <span className="balance-label">Shitcoins (Buinkers Game)</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions Section */}
                    <div className="dashboard-section actions-section">
                        <div className="action-card deposit-card">
                            <h3>Deposit TON</h3>
                            <p className="hint">Fund your virtual wallet to make purchases.</p>
                            <button className="secondary-btn" onClick={() => copyAddress(user.virtualWalletId as string || "")} disabled={!user.virtualWalletId}>
                                Copy Virtual Address
                            </button>
                        </div>

                        <div className="action-card withdraw-card">
                            <h3>Withdraw TON</h3>
                            <div className="withdraw-form">
                                <input
                                    type="text"
                                    placeholder="Destination Address"
                                    value={withdrawAddress}
                                    onChange={(e) => setWithdrawAddress(e.target.value)}
                                    className="modern-input"
                                />
                                <div className="input-with-suffix">
                                    <input
                                        type="number"
                                        placeholder="Amount"
                                        value={withdrawAmount}
                                        onChange={(e) => setWithdrawAmount(e.target.value)}
                                        className="modern-input"
                                    />
                                    <span className="input-suffix">TON</span>
                                </div>
                                <button
                                    className="action-btn withdraw-btn"
                                    onClick={handleWithdraw}
                                    disabled={withdrawing || !withdrawAddress || !withdrawAmount}
                                >
                                    {withdrawing ? "Processing..." : "Withdraw Funds"}
                                </button>
                            </div>
                        </div>

                        <div className="action-card convert-card">
                            <h3>Convert Shitcoin</h3>
                            <p className="hint">Convert shitcoins from Buinkers to Buink.</p>
                            <div className="withdraw-form">
                                <div className="input-with-suffix">
                                    <input
                                        type="number"
                                        placeholder="Shitcoin Amount"
                                        value={convertAmount}
                                        onChange={(e) => setConvertAmount(e.target.value)}
                                        className="modern-input"
                                    />
                                    <span className="input-suffix">💩</span>
                                </div>
                                <button
                                    className="action-btn"
                                    style={{ background: 'var(--primary-color)', color: 'black' }}
                                    onClick={handleConvert}
                                    disabled={converting || !convertAmount}
                                >
                                    {converting ? "Converting..." : "Convert to Buink"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
